import { baseApi } from './baseApi';
import { normalizeKeys } from '../../utils/transform';

export interface OrgModelUsedBy {
  agent_id: string;
  name: string;
}

export interface ModelBenchmark {
  key: string;
  /** Plain-language dimension: Reasoning | Coding | Knowledge. */
  label: string;
  /** Benchmark name, e.g. GPQA Diamond. */
  name: string;
  score: number;
  unit: string;
  sourceUrl: string;
  /** YYYY-MM */
  asOf: string;
}

/** 1 (lowest) … 4 (highest) relative cost; null when not priced. */
export type CostTier = 1 | 2 | 3 | 4;

export interface OrgModel {
  productId: string;
  displayName: string;
  /** @deprecated Prefer `route`. Alias of route for one release. */
  provider: string;
  lab?: string;
  route?: string;
  family?: string;
  capabilities: string[];
  enabled: boolean;
  chatCapable?: boolean;
  /** Set when turning the model on is rejected until a payment method exists. */
  enableBlockedReason?: string | null;
  usedAsDefaultBy?: OrgModelUsedBy[];
  costTier?: CostTier | null;
  contextWindow?: number | null;
  maxOutputTokens?: number | null;
  bestFor?: string[];
  benchmarks?: ModelBenchmark[];
  task?: string;
  modelKey?: string;
  wayLabel?: string;
  features?: string[];
  tags?: string[];
  dataLocation?: string | null;
  addedOn?: string | null;
  openWeights?: boolean;
  searchNote?: string | null;
  lifecycle?: { stage?: string; retiresOn?: string | null; replacement?: string | null };
  speed?: {
    p50LatencyMs?: number | null;
    p50TtftMs?: number | null;
    outputTokensPerSecond?: number | null;
    windowDays?: number;
    measured?: boolean;
    class?: string | null;
  } | null;
  specs?: Record<string, unknown>;
}

export interface OrgModelDetail extends OrgModel {
  usedBy: {
    agents: { agentId: string; name: string; role: 'default' | 'recommended' }[];
    applications: { applicationId: string; name: string }[];
  };
}

export interface OrgModelsResponse {
  models: OrgModel[];
}

export interface PatchOrgModelRequest {
  orgId: string;
  productId: string;
  enabled: boolean;
  confirmInUse?: boolean;
}

export const orgModelsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getOrgModels: builder.query<OrgModel[], string>({
      query: (orgId) => `/portal/organisations/${orgId}/models`,
      transformResponse: (response: unknown) => {
        const normalized = normalizeKeys<OrgModelsResponse>(response);
        return normalized.models ?? [];
      },
      providesTags: ['OrgModel'],
    }),

    getOrgModel: builder.query<OrgModelDetail, { orgId: string; productId: string }>({
      query: ({ orgId, productId }) =>
        `/portal/organisations/${orgId}/models/${encodeURIComponent(productId)}`,
      transformResponse: (response: unknown) => {
        const model = normalizeKeys<OrgModelDetail>(response);
        return {
          ...model,
          usedBy: {
            agents: model.usedBy?.agents ?? [],
            applications: model.usedBy?.applications ?? [],
          },
        };
      },
      providesTags: ['OrgModel'],
    }),

    patchOrgModel: builder.mutation<OrgModel, PatchOrgModelRequest>({
      query: ({ orgId, productId, enabled, confirmInUse }) => ({
        url: `/portal/organisations/${orgId}/models/${encodeURIComponent(productId)}`,
        method: 'PATCH',
        body: {
          enabled,
          ...(confirmInUse ? { confirm_in_use: true } : {}),
        },
      }),
      transformResponse: (response: unknown) => normalizeKeys<OrgModel>(response),
      invalidatesTags: ['OrgModel'],
    }),
  }),
});

export const { useGetOrgModelsQuery, useGetOrgModelQuery, usePatchOrgModelMutation } =
  orgModelsApi;
